import { useEffect, useState } from 'react';
import { GuildMark } from './GuildMark';
import { fetchGuildIdentity } from '../lib/api';
import { APP_NAME, cacheGuildIdentity, cachedGuildIcon, cachedGuildName } from '../lib/brand';
import { AuthError, NewPasswordRequired, completeNewPassword, signIn, store } from '../lib/cognito';
import { beginGoogleSignIn, completeSignInFromRedirect, oauthConfigured } from '../lib/oauth';

/**
 * One way in: Google. No username to invent, no password to forget, and the
 * email Google verifies is the same email the guild's invite list is keyed on.
 *
 * The password form still exists but is not offered. It is the way back in if
 * the Google configuration ever breaks — losing the identity provider and
 * losing all access to the league should not be the same event — and it is the
 * only path available before Google has been wired up at all.
 */
export function SignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [challenge, setChallenge] = useState<NewPasswordRequired | null>(null);
  const [busy, setBusy] = useState(false);
  const [returning, setReturning] = useState(() =>
    new URLSearchParams(window.location.search).has('code'),
  );
  const [error, setError] = useState<string | null>(null);

  const google = oauthConfigured();

  /**
   * Start from whatever the last visit cached, then ask the server.
   *
   * Somebody opening their invite for the first time has no cache at all, so
   * without the fetch they would be asked to join a guild identified only by
   * two letters in a coloured square.
   */
  const [guild, setGuild] = useState(() => ({
    name: cachedGuildName(),
    icon: cachedGuildIcon(),
  }));

  useEffect(() => {
    let cancelled = false;
    void fetchGuildIdentity()
      .then((identity) => {
        if (cancelled) return;
        setGuild({ name: identity.guildName, icon: identity.guildIcon });
        cacheGuildIdentity(identity.guildName, identity.guildIcon);
      })
      .catch(() => {
        // Offline, or the API is down. The cached values are still shown, and
        // nothing on this screen depends on them being current.
      });
    return () => {
      cancelled = true;
    };
  }, []);
  // Shown automatically when there is no Google to offer, and reachable at
  // ?password=1 the rest of the time as the escape hatch. Never toggled, so it
  // is derived rather than held.
  const showPassword = !google || new URLSearchParams(window.location.search).has('password');

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const tokens = await completeSignInFromRedirect();
        if (!tokens || cancelled) return;
        store({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          expiresAt: tokens.expiresAt,
          username: 'google',
          source: 'google',
        });
        onSignedIn();
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'sign-in failed');
      } finally {
        if (!cancelled) setReturning(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [onSignedIn]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (challenge) {
        await completeNewPassword(challenge.session, challenge.username, newPassword);
      } else {
        await signIn(username.trim(), password);
      }
      onSignedIn();
    } catch (err) {
      if (err instanceof NewPasswordRequired) {
        setChallenge(err);
        setError(null);
      } else {
        setError(err instanceof AuthError ? err.message : 'could not sign in');
      }
    } finally {
      setBusy(false);
    }
  }

  if (returning) {
    return (
      <div className="signin" style={{ alignItems: 'center' }}>
        <span className="spinner" />
      </div>
    );
  }

  return (
    <div className="signin">
      {/* Read from the last visit, since this screen renders before there is a
          token to ask the server with. */}
      <div className="signin-head">
        <GuildMark name={guild.name} src={guild.icon} size={64} />
        <div>
          <h1>{APP_NAME}</h1>
          <p className="guild">{guild.name}</p>
        </div>
      </div>

      {error && <div className="banner">{error}</div>}

      {google && !challenge && (
        <>
          <button
            className="btn primary block"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}
            onClick={() => void beginGoogleSignIn()}
          >
            <GoogleMark />
            Continue with Google
          </button>

          {/* Multiple guilds are not built. The option is shown rather than
              hidden so the shape of the app is honest about where it is going,
              and disabled so it cannot lie about what it does today. */}
          <button className="btn block" style={{ marginTop: 10 }} disabled>
            Start a guild
          </button>
          <p className="hint" style={{ textAlign: 'center', marginTop: 10 }}>
            You need an invite to join a guild. Starting your own is coming later.
          </p>
        </>
      )}

      {(showPassword || challenge) && (
        <form onSubmit={submit} style={{ marginTop: google ? 24 : 0 }}>
          {google && (
            <p className="hint" style={{ marginBottom: 12 }}>
              Password sign-in, for when Google is unavailable.
            </p>
          )}
          {challenge ? (
            <label className="field">
              <span>New password</span>
              <input
                className="input"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={10}
                autoFocus
              />
            </label>
          ) : (
            <>
              <label className="field">
                <span>Name</span>
                <input
                  className="input"
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </label>
              <label className="field">
                <span>Password</span>
                <input
                  className="input"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </label>
            </>
          )}

          <button className={`btn block ${google ? '' : 'primary'}`} type="submit" disabled={busy}>
            {busy ? <span className="spinner" /> : challenge ? 'Set password' : 'Sign in'}
          </button>
        </form>
      )}

      {!google && (
        <p className="hint" style={{ textAlign: 'center', marginTop: 18 }}>
          Google sign-in is not configured yet.
        </p>
      )}
    </div>
  );
}

/** Google's mark, inline — an external asset would be blocked offline anyway. */
function GoogleMark() {
  return (
    <svg width="17" height="17" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#4285F4" d="M45.1 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h11.8c-.5 2.7-2 5-4.4 6.6v5.5h7.1c4.2-3.8 6.6-9.5 6.6-16.1z" />
      <path fill="#34A853" d="M24 46c6 0 11-2 14.5-5.4l-7.1-5.5c-2 1.3-4.5 2.1-7.4 2.1-5.7 0-10.6-3.9-12.3-9.1H4.3v5.7C7.8 41 15.3 46 24 46z" />
      <path fill="#FBBC05" d="M11.7 28.1c-.4-1.3-.7-2.7-.7-4.1s.2-2.8.7-4.1v-5.7H4.3C2.8 17.1 2 20.4 2 24s.8 6.9 2.3 9.8l7.4-5.7z" />
      <path fill="#EA4335" d="M24 10.8c3.2 0 6.1 1.1 8.4 3.3l6.3-6.3C34.9 4.2 30 2 24 2 15.3 2 7.8 7 4.3 14.2l7.4 5.7c1.7-5.2 6.6-9.1 12.3-9.1z" />
    </svg>
  );
}
